import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { stripeInputStyles, stripeLabelStyles, stripeSelectStyles } from '../hub-utils';
import { statesList } from '../../shared/misc/us-states';

export interface AddressDetails {
  streetAddress1: string;
  city: string;
  state: string;
  zipCode: string;
}

export const isAddressComplete = (address: AddressDetails): boolean => {
  return !!(
    address.streetAddress1?.trim() &&
    address.city?.trim() &&
    address.state?.trim() &&
    address.zipCode?.trim()
  );
};

interface GuarantorAddressFieldsProps {
  addressDetails: AddressDetails;
  onAddressDetailsChange: (field: keyof AddressDetails, value: string) => void;
  testIdPrefix?: string;
}

interface TouchedFields {
  streetAddress1: boolean;
  city: boolean;
  state: boolean;
  zipCode: boolean;
}

const GuarantorAddressFields: React.FC<GuarantorAddressFieldsProps> = ({
  addressDetails,
  onAddressDetailsChange,
  testIdPrefix = '',
}) => {
  const [touched, setTouched] = useState<TouchedFields>({
    streetAddress1: false,
    city: false,
    state: false,
    zipCode: false,
  });

  const getTestId = (field: string) => testIdPrefix ? `${testIdPrefix}-${field}` : field;

  const handleBlur = (field: keyof AddressDetails) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const getError = (field: keyof AddressDetails): string | null => {
    if (!touched[field]) return null;
    if (!addressDetails[field]?.trim()) {
      const fieldLabels: Record<keyof AddressDetails, string> = {
        streetAddress1: 'Street address',
        city: 'City',
        state: 'State',
        zipCode: 'Zip code',
      };
      return `${fieldLabels[field]} is required`;
    }
    return null;
  };

  const getInputStyle = (field: keyof AddressDetails) => ({
    ...stripeInputStyles,
    borderColor: getError(field) ? '#df1b41' : '#e6e6e6',
  });

  const getSelectStyle = (field: keyof AddressDetails) => ({
    ...stripeSelectStyles,
    borderColor: getError(field) ? '#df1b41' : '#e6e6e6',
  });

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ mb: 2 }}>
        <Typography component="label" sx={stripeLabelStyles}>
          Street Address
        </Typography>
        <input
          type="text"
          value={addressDetails.streetAddress1}
          onChange={(e) => onAddressDetailsChange('streetAddress1', e.target.value)}
          onBlur={() => handleBlur('streetAddress1')}
          placeholder="Enter street address"
          style={getInputStyle('streetAddress1')}
          test-id={getTestId('street-address')}
        />
        {getError('streetAddress1') && (
          <Typography sx={{ color: '#df1b41', fontSize: '12px', mt: 0.5 }}>
            {getError('streetAddress1')}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Box sx={{ flex: 2 }}>
          <Typography component="label" sx={stripeLabelStyles}>
            City
          </Typography>
          <input
            type="text"
            value={addressDetails.city}
            onChange={(e) => onAddressDetailsChange('city', e.target.value)}
            onBlur={() => handleBlur('city')}
            placeholder="Enter city"
            style={getInputStyle('city')}
            test-id={getTestId('city')}
          />
          {getError('city') && (
            <Typography sx={{ color: '#df1b41', fontSize: '12px', mt: 0.5 }}>
              {getError('city')}
            </Typography>
          )}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography component="label" sx={stripeLabelStyles}>
            State
          </Typography>
          <select
            value={addressDetails.state}
            onChange={(e) => onAddressDetailsChange('state', e.target.value)}
            onBlur={() => handleBlur('state')}
            style={getSelectStyle('state')}
            test-id={getTestId('state')}
          >
            <option value="">Select</option>
            {statesList.map((state) => (
              <option key={state.stateCode} value={state.stateCode}>
                {state.stateCode}
              </option>
            ))}
          </select>
          {getError('state') && (
            <Typography sx={{ color: '#df1b41', fontSize: '12px', mt: 0.5 }}>
              {getError('state')}
            </Typography>
          )}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography component="label" sx={stripeLabelStyles}>
            Zip Code
          </Typography>
          <input
            type="text"
            value={addressDetails.zipCode}
            onChange={(e) => onAddressDetailsChange('zipCode', e.target.value)}
            onBlur={() => handleBlur('zipCode')}
            placeholder="Zip"
            style={getInputStyle('zipCode')}
            maxLength={5}
            test-id={getTestId('zip-code')}
          />
          {getError('zipCode') && (
            <Typography sx={{ color: '#df1b41', fontSize: '12px', mt: 0.5 }}>
              {getError('zipCode')}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default GuarantorAddressFields;
